// One map from a flow mutation to the queries it must refresh. Hooks call
// `applyInvalidations` with a row from here; they do not list keys themselves.
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import { merchantFlowKeys } from "./merchantFlowKeys";

const keys = {
  list: merchantFlowKeys.list,
  checklist: (borrowerId: number) => queryKeys.borrowerChecklist(borrowerId),
  defaults: merchantFlowKeys.defaultsPrefix,
  /** Deal/Plaid payload Final Underwriting reads. Checklist is a nested key, not this one. */
  finalUw: (borrowerId: number) => queryKeys.borrower(borrowerId),
  detail: merchantFlowKeys.detail,
  preview: merchantFlowKeys.previewPrefix,
  oneOffUploads: merchantFlowKeys.oneOffUploads,
  documents: merchantFlowKeys.documentsPrefix,
  /** A refinance send stamps its fee on the offer, a new version the open form must load. */
  refinance: (borrowerId: number) => queryKeys.refinanceOfferByBorrower(borrowerId),
};

export const INVALIDATES = {
  /** A Custom upload send uses its upload up, so the card stops offering it. */
  send: (borrowerId: number): QueryKey[] => [
    keys.list(borrowerId),
    keys.checklist(borrowerId),
    keys.defaults(borrowerId),
    keys.refinance(borrowerId),
    keys.oneOffUploads(borrowerId),
  ],
  invalidate: (borrowerId: number, flowId: number): QueryKey[] => [
    keys.list(borrowerId),
    keys.checklist(borrowerId),
    keys.defaults(borrowerId),
    keys.detail(flowId),
  ],
  resend: (borrowerId: number): QueryKey[] => [keys.list(borrowerId)],
  /** Also unlinks the ID's upload: the list's document counts and open flows' files. */
  resetId: (borrowerId: number): QueryKey[] => [
    keys.list(borrowerId),
    keys.documents(),
    keys.checklist(borrowerId),
    keys.defaults(borrowerId),
    keys.finalUw(borrowerId),
  ],
  revokeLink: (_borrowerId: number, flowId: number): QueryKey[] => [keys.detail(flowId)],
  /** Editing a one-off's fields changes the card's upload and the preview's checks. */
  oneOff: (borrowerId: number): QueryKey[] => [
    keys.defaults(borrowerId),
    keys.preview(borrowerId),
    keys.oneOffUploads(borrowerId),
  ],
  /**
   * Uploading or removing the one-off changes the card's upload. The preview is left out:
   * the draft follows the new defaults to a new key, and refetching the old one would
   * preview an id the server has just dropped.
   */
  oneOffChanged: (borrowerId: number): QueryKey[] => [
    keys.defaults(borrowerId),
    keys.oneOffUploads(borrowerId),
  ],
  /**
   * The one-off was sent or removed elsewhere: the card follows the defaults, and one sent
   * shows in the list. The preview is left out for the same reason as `oneOffChanged`.
   */
  oneOffGone: (borrowerId: number): QueryKey[] => [
    keys.list(borrowerId),
    keys.defaults(borrowerId),
    keys.oneOffUploads(borrowerId),
  ],
  /** Signed-disclosure facts. The draft preview must refetch; the flow list does not change. */
  invalidateDisclosure: (borrowerId: number): QueryKey[] => [
    keys.checklist(borrowerId),
    keys.defaults(borrowerId),
    keys.preview(borrowerId),
  ],
} as const;

export type FlowMutation = keyof typeof INVALIDATES;

export function applyInvalidations(queryClient: QueryClient, queryKeysToDrop: readonly QueryKey[]) {
  void refetchInvalidated(queryClient, queryKeysToDrop);
}

/** `applyInvalidations` for a mutation that stays pending until the card shows its result. */
export async function refetchInvalidated(
  queryClient: QueryClient,
  queryKeysToDrop: readonly QueryKey[]
): Promise<void> {
  await Promise.all(queryKeysToDrop.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}
